import { useEffect, useState } from 'react';
import { Button } from '../ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog.tsx';
import { Badge } from '../ui/badge.tsx';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  User,
  FileText,
} from 'lucide-react';
import { useGetEventsV2 } from '../../api/events/eventQueries.js';
import {
  getWeekStart,
  getEventPosition,
  getHours,
  getWeekDays,
  formatTime,
} from '../../utils/scheduleUtils.js';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';

export default function Schedule({ staffId = '' }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentDateStart, setCurrentDateStart] = useState('');
  const [currentDateEnd, setCurrentDateEnd] = useState('');
  const [view, setView] = useState('weekly');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // UseEffect to fetch events when currentDate or view changes
  useEffect(() => {
    if (view === 'weekly') {
      const weekStartDate = getWeekStart(currentDate);
      const weekEndDate = new Date(
        weekStartDate.getTime() + 6 * 24 * 60 * 60 * 1000
      );
      setCurrentDateStart(weekStartDate.toISOString().split('T')[0]);
      setCurrentDateEnd(weekEndDate.toISOString().split('T')[0]);
    } else {
      const yearStartDate = new Date(currentDate.getFullYear(), 0, 2);
      const yearEndDate = new Date(currentDate.getFullYear(), 11, 32);
      setCurrentDateStart(yearStartDate.toISOString().split('T')[0]);
      setCurrentDateEnd(yearEndDate.toISOString().split('T')[0]);
    }
  }, [currentDate, view]);

  const {
    data: events,
    isLoading,
    isError,
    refetch,
  } = useGetEventsV2(currentDateStart, currentDateEnd, staffId);

  const sampleEvents = [
    {
      eventId: '1',
      eventType: 'SCHEDULE',
      eventTitle: 'Team Standup',
      eventDate: new Date(2025, 0, 20),
      startTime: new Date(2025, 0, 20, 9, 0),
      endTime: new Date(2025, 0, 20, 9, 30),
      recurring: true,
      recurrenceRule: 'FREQ=DAILY;BYDAY=MO,TU,WE,TH,FR',
      location: 'Conference Room A',
      assignee: 'john-doe',
      description:
        'Daily team standup meeting to discuss progress and blockers',
      organizationId: 'org-1',
    },
    {
      eventId: '2',
      eventType: 'EVENT',
      eventTitle: 'Client Meeting',
      eventDate: new Date(2025, 0, 21),
      startTime: new Date(2025, 0, 21, 14, 0),
      endTime: new Date(2025, 0, 21, 15, 30),
      recurring: false,
      location: 'Zoom',
      description: 'Quarterly review meeting with client',
      organizationId: 'org-1',
    },
    {
      eventId: '3',
      eventType: 'TASK',
      eventTitle: 'Code Review',
      eventDate: new Date(2025, 0, 22),
      startTime: new Date(2025, 0, 22, 11, 0),
      endTime: new Date(2025, 0, 22, 12, 0),
      recurring: false,
      location: 'Development Office',
      assignee: 'jane-smith',
      description: 'Review pull requests and provide feedback',
      organizationId: 'org-1',
    },
    {
      eventId: '4',
      eventType: 'EVENT',
      eventTitle: 'Project Deadline',
      eventDate: new Date(2025, 1, 15),
      startTime: new Date(2025, 1, 15, 16, 0),
      endTime: new Date(2025, 1, 15, 16, 30),
      recurring: false,
      location: 'Office',
      description: 'Final submission for Q1 project deliverables',
      organizationId: 'org-1',
    },
    {
      eventId: '5',
      eventType: 'SCHEDULE',
      eventTitle: 'All Hands Meeting',
      eventDate: new Date(2025, 2, 10),
      startTime: new Date(2025, 2, 10, 10, 0),
      endTime: new Date(2025, 2, 10, 11, 0),
      recurring: true,
      recurrenceRule: 'FREQ=MONTHLY;BYMONTHDAY=10',
      location: 'Main Auditorium',
      description:
        'Monthly company-wide meeting to discuss updates and announcements',
      organizationId: 'org-1',
    },
    {
      eventId: '6',
      eventType: 'EVENT',
      eventTitle: 'Doctor Appointment',
      eventDate: new Date(2025, 3, 5),
      startTime: new Date(2025, 3, 5, 13, 0),
      endTime: new Date(2025, 3, 5, 14, 0),
      recurring: false,
      location: 'Medical Center',
      description: 'Annual health checkup appointment',
      organizationId: 'org-1',
    },
  ];

  // const allEvents = [...events.data, ...sampleEvents];
  const allEvents = [...sampleEvents];

  const getEventsForDay = (date) => {
    return events.data.filter((event) => {
      const eventDate = new Date(event.eventDate);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const navigateToSpecificWeek = (date) => {
    setCurrentDate(new Date(date));
    setView('weekly');
  };

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'SCHEDULE':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'TASK':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'EVENT':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const weekStart = getWeekStart(currentDate);
  const weekDays = getWeekDays(weekStart);
  const hours = getHours();

  if (!isError) {
    if (view === 'yearly') {
      const months = Array.from({ length: 12 }, (_, i) => {
        const date = new Date(currentDate.getFullYear(), i, 1);
        return date;
      });

      return (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div />
            <div className="flex items-center space-x-2">
              <Button
                variant={view === 'weekly' ? 'default' : 'outline'}
                onClick={() => setView('weekly')}
                className={
                  view === 'weekly' ? 'bg-purple-600 hover:bg-purple-700' : ''
                }
              >
                Weekly
              </Button>
              <Button
                variant={view === 'yearly' ? 'default' : 'outline'}
                onClick={() => setView('yearly')}
                className={
                  view === 'yearly' ? 'bg-purple-600 hover:bg-purple-700' : ''
                }
              >
                Yearly
              </Button>
            </div>
          </div>

          {/* Year Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear() - 1,
                    currentDate.getMonth(),
                    1
                  )
                )
              }
              className="flex items-center space-x-2"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous Year</span>
            </Button>
            <h2 className="text-xl font-semibold">
              {currentDate.getFullYear()}
            </h2>
            <Button
              variant="outline"
              onClick={() =>
                setCurrentDate(
                  new Date(
                    currentDate.getFullYear() + 1,
                    currentDate.getMonth(),
                    1
                  )
                )
              }
              className="flex items-center space-x-2"
            >
              <span>Next Year</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Yearly Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {months.map((month, index) => (
                  <Card
                    key={index}
                    className="hover:shadow-md transition-shadow hover:border-purple-300"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-center">
                        {month.toLocaleDateString([], { month: 'long' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-7 gap-1 text-xs">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day) => (
                          <div
                            key={day}
                            className="text-center font-medium text-gray-500 p-1"
                          >
                            {day}
                          </div>
                        ))}
                        {(() => {
                          const firstDay = new Date(
                            month.getFullYear(),
                            month.getMonth(),
                            1
                          );

                          const startDate = new Date(firstDay);
                          startDate.setDate(
                            startDate.getDate() - firstDay.getDay()
                          );

                          const days = [];
                          for (let i = 0; i < 42; i++) {
                            const currentDate = new Date(startDate);
                            currentDate.setDate(startDate.getDate() + i);

                            const isCurrentMonth =
                              currentDate.getMonth() === month.getMonth();
                            const hasEvents =
                              getEventsForDay(currentDate).length > 0;

                            days.push(
                              <div
                                key={i}
                                className={`text-center p-1 rounded cursor-pointer hover:bg-purple-50 transition-colors ${
                                  !isCurrentMonth
                                    ? 'text-gray-300'
                                    : hasEvents
                                    ? 'bg-purple-100 text-purple-800 font-medium hover:bg-purple-200'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                onClick={() =>
                                  navigateToSpecificWeek(currentDate)
                                }
                              >
                                {currentDate.getDate()}
                              </div>
                            );
                          }
                          return days.slice(0, 35); // Show 5 weeks
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div />
          <div className="flex items-center space-x-2">
            <Button
              variant={view === 'weekly' ? 'default' : 'outline'}
              onClick={() => setView('weekly')}
              className={
                view === 'weekly' ? 'bg-purple-600 hover:bg-purple-700' : ''
              }
            >
              Weekly
            </Button>
            <Button
              variant={view === 'yearly' ? 'default' : 'outline'}
              onClick={() => setView('yearly')}
              className={
                view === 'yearly' ? 'bg-purple-600 hover:bg-purple-700' : ''
              }
            >
              Yearly
            </Button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Week Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => navigateWeek('prev')}
                className="flex items-center space-x-2"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous Week</span>
              </Button>
              <h2 className="md:text-xl font-semibold hidden md:block">
                {weekStart.toLocaleDateString([], {
                  month: 'long',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {new Date(
                  weekStart.getTime() + 6 * 24 * 60 * 60 * 1000
                ).toLocaleDateString([], {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h2>
              <Button
                variant="outline"
                onClick={() => navigateWeek('next')}
                className="flex items-center space-x-2"
              >
                <span>Next Week</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Weekly Calendar */}
            <Card>
              <CardContent className="p-0">
                <div className="grid grid-cols-8 border-b">
                  <div className="p-4 border-r bg-gray-50"></div>
                  {weekDays.map((day, index) => (
                    <div
                      key={index}
                      className="p-4 text-center border-r last:border-r-0"
                    >
                      <div className="font-medium text-gray-900">
                        {day.toLocaleDateString([], { weekday: 'short' })}
                      </div>
                      <div className="text-sm text-gray-600">
                        {day.getDate()}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-8">
                  {/* Time column */}
                  <div className="border-r bg-gray-50">
                    {hours.map((hour) => (
                      <div
                        key={hour}
                        className="h-16 border-b flex items-center justify-center text-sm text-gray-600"
                      >
                        {hour === 12
                          ? '12 PM'
                          : hour > 12
                          ? `${hour - 12} PM`
                          : `${hour} AM`}
                      </div>
                    ))}
                  </div>

                  {/* Day columns */}
                  {weekDays.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className="border-r last:border-r-0 relative"
                    >
                      {hours.map((hour) => (
                        <div key={hour} className="h-16 border-b"></div>
                      ))}

                      {/* Events for this day */}
                      {getEventsForDay(day).map((event) => {
                        const position = getEventPosition(event);
                        return (
                          <div
                            key={event.eventId}
                            className="absolute left-1 right-1 bg-purple-600 text-white text-xs p-1 rounded cursor-pointer hover:bg-purple-700 transition-colors z-10"
                            style={{
                              top: `${position.top}px`,
                              height: `${position.height}px`,
                            }}
                            onClick={() => openEventModal(event)}
                          >
                            <div className="font-medium truncate">
                              {event.eventTitle}
                            </div>
                            <div className="text-purple-100 truncate">
                              {formatTime(event.startTime)} -{' '}
                              {formatTime(event.endTime)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Event Details Modal */}
        <Dialog open={isEventModalOpen} onOpenChange={setIsEventModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <span>Event Details</span>
              </DialogTitle>
            </DialogHeader>

            {selectedEvent && (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">
                    {selectedEvent.eventTitle}
                  </h3>
                  <Badge className={getEventTypeColor(selectedEvent.eventType)}>
                    {selectedEvent.eventType}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span>
                      {new Date(selectedEvent.eventDate).toLocaleDateString()} •{' '}
                      {formatTime(selectedEvent.startTime)} -{' '}
                      {formatTime(selectedEvent.endTime)}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span>{selectedEvent.location}</span>
                  </div>

                  {selectedEvent.assignee && (
                    <div className="flex items-center space-x-2 text-sm">
                      <User className="h-4 w-4 text-gray-500" />
                      <span>Assigned to: {selectedEvent.assignee}</span>
                    </div>
                  )}

                  {selectedEvent.recurring && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span>Recurring: {selectedEvent.recurrenceRule}</span>
                    </div>
                  )}

                  <div className="flex items-start space-x-2 text-sm">
                    <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                    <span>{selectedEvent.description}</span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  } else {
    return (
      <div className="m-4 w-full flex flex-col justify-center">
        <p className="text-center">There was an error retrieving events</p>
        <Button
          onClick={() => {
            refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }
}
